import {ExponentialBackoffOptions} from '../../../helpers/controlHelpers';
import {
  InboxesFiltersEnum,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
} from '../../../models/url/inboxesUrlModel';
import {AppState} from '../../../store/appState';
import {getSortOptions} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {
  InboxesParametersTypesEnum,
  InboxesTeammateMessagesParameters,
  InboxesUserParameters,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersFromTemplate} from '../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {Prefetcher} from './base';
import {createInitialCellsPrefetcher} from './initialCells';

/*
 * Factories.
 */

/** Prefetches the first page of a user inbox folder. */
export function createUserInboxInitialCellsPrefetcher(
  initialState: AppState,
  filter: InboxesFiltersEnum,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Prefetcher<void> {
  const parameters = buildUserInboxCellsParameters(initialState, filter);

  return createInitialCellsPrefetcher({
    key: `initial-user-inbox-cells-${filter}`,
    parameters,
    exponentialBackoffOptions,
  });
}

/** Prefetches the first page of the user messages folder. */
export function createUserMessagesInitialCellsPrefetcher(
  initialState: AppState,
  folder: TeammatesMessageFoldersEnum,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Prefetcher<void> {
  const parameters = buildUserMessagesCellsParameters(initialState, folder);

  return createInitialCellsPrefetcher({
    key: `initial-user-messages-cells-${folder}`,
    parameters,
    exponentialBackoffOptions,
  });
}

/*
 * Helpers.
 */

function buildUserInboxCellsParameters(state: AppState, filter: InboxesFiltersEnum): InboxesUserParameters {
  const userId = getUserId(state);
  const sortOptions = getSortOptions(state);

  return buildParametersFromTemplate(sortOptions, {
    type: InboxesParametersTypesEnum.USER,
    teammateId: userId,
    folder: TeammatesFoldersEnum.INBOX,
    filters: [filter],
    selection: emptyInboxesSelection,
  });
}

function buildUserMessagesCellsParameters(
  state: AppState,
  folder: TeammatesMessageFoldersEnum,
): InboxesTeammateMessagesParameters {
  const userId = getUserId(state);
  const sortOptions = getSortOptions(state);

  return buildParametersFromTemplate(sortOptions, {
    type: InboxesParametersTypesEnum.TEAMMATE_MESSAGES,
    teammateId: userId,
    folder,
    selection: emptyInboxesSelection,
    hasFindMyDrafts2: false,
    hasListSendingMessagesInSentFolder: false,
  });
}
